import { KaboodleToaster } from "components/common";
import {
  type CloneReason,
  CLONE_REASON_OPTIONS,
  DEFAULT_CLONE_REASON,
} from "pages/metadoc/MetadocConstants";
import { useMemo, useRef, useState } from "react";
import type { Metadoc } from "services/metadoc-api";
import { useCloneMetadocMutation } from "services/metadoc-api";
import { extractErrorMessage } from "utils/error-utils";

import { CloneMetadocModal } from "./CloneMetadocModal";
import { metadocNameSchema } from "./metadocNameValidation";

type CloneMetadocButtonProps = {
  metadoc: Metadoc;
  isCloneInProgressFromJobs?: boolean;
};

/*
 * orchestrates clone form state, validation, and clone mutation.
 */
export const CloneMetadocButton = ({
  metadoc,
  isCloneInProgressFromJobs = false,
}: CloneMetadocButtonProps) => {
  const [isCloneModalOpen, setIsCloneModalOpen] = useState(false);
  const [selectedCloneReason, setSelectedCloneReason] =
    useState<CloneReason>(DEFAULT_CLONE_REASON);
  const defaultCloneName = useMemo(
    () => `${metadoc.name} (Copy)`,
    [metadoc.name],
  );
  const [cloneNameInput, setCloneNameInput] = useState(defaultCloneName);
  const [cloneMetadoc, { isLoading: isCloning }] = useCloneMetadocMutation();
  const isSubmittingCloneRef = useRef(false);

  const hasAiRun = Boolean(metadoc.pre_ai_run_download_url);
  const isCloneInProgress = isCloning || isCloneInProgressFromJobs;

  const handleOpenModal = (event: React.MouseEvent) => {
    event.preventDefault();
    event.stopPropagation();

    if (isCloneInProgress) {
      KaboodleToaster.info("A clone is already in progress for this metadoc.");
      return;
    }

    setSelectedCloneReason(DEFAULT_CLONE_REASON);
    setCloneNameInput(defaultCloneName);
    setIsCloneModalOpen(true);
  };

  const handleCloneConfirm = async () => {
    if (isSubmittingCloneRef.current || isCloneInProgress) {
      KaboodleToaster.info("A clone is already in progress for this metadoc.");
      return;
    }

    isSubmittingCloneRef.current = true;

    try {
      const nameValidation = metadocNameSchema.safeParse(cloneNameInput);
      if (!nameValidation.success) {
        KaboodleToaster.error(
          nameValidation.error.issues[0]?.message ?? "Invalid clone name",
        );
        return;
      }

      setIsCloneModalOpen(false);
      KaboodleToaster.info(
        "Cloning Meta Document. It should appear in the list shortly.",
      );

      await cloneMetadoc({
        metadocId: metadoc.id,
        cloneName: nameValidation.data,
        ...(hasAiRun ? { cloneReason: selectedCloneReason } : {}),
      }).unwrap();
    } catch (error) {
      KaboodleToaster.error(
        extractErrorMessage(error, "Failed to clone Meta Document"),
      );
    } finally {
      isSubmittingCloneRef.current = false;
    }
  };

  return (
    <>
      <span
        className={`material-icons action-icon ${
          isCloneInProgress ? "action-icon--spinning" : ""
        }`}
        data-tooltip-id="metadoc-clone-tooltip"
        data-tooltip-content={
          isCloneInProgress ? "Clone in progress" : "Clone Metadoc"
        }
        onClick={handleOpenModal}
        style={{
          cursor: isCloneInProgress ? "not-allowed" : "pointer",
        }}
      >
        {isCloneInProgress ? "autorenew" : "content_copy"}
      </span>

      <CloneMetadocModal
        isOpen={isCloneModalOpen}
        onClose={() => setIsCloneModalOpen(false)}
        onConfirm={handleCloneConfirm}
        metadocId={metadoc.id}
        metadocName={metadoc.name}
        cloneNameInput={cloneNameInput}
        onCloneNameChange={setCloneNameInput}
        defaultCloneName={defaultCloneName}
        hasAiRun={hasAiRun}
        selectedCloneReason={selectedCloneReason}
        onCloneReasonChange={setSelectedCloneReason}
        cloneReasonOptions={CLONE_REASON_OPTIONS}
        isCloneInProgress={isCloneInProgress}
      />
    </>
  );
};
